// YouTube chapter checker (pure): YouTube only shows chapters when the description's timestamps start
// at 0:00, there are at least 3, they go in order, and each chapter lasts at least 10 seconds. This
// finds what breaks those rules and builds a fixed list where it can.

const LINE = /^\s*(?:[-–—•*>]\s*)?\(?\[?((?:\d{1,2}:)?\d{1,2}:\d{2})\]?\)?\s*(?:[-–—:|.]\s*)?(.*)$/;

export function parseStamp(stamp) {
  const parts = String(stamp).split(':').map(Number);
  if (parts.length < 2 || parts.length > 3 || parts.some(n => !Number.isInteger(n) || n < 0)) return null;
  if (parts.slice(1).some(n => n > 59)) return null;
  return parts.reduce((acc, n) => acc * 60 + n, 0);
}

export function clock(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

// duration: the video's length in seconds, optional
export function checkChapters(text, duration = 0) {
  const issues = [];
  const chapters = [];
  (text || '').split('\n').forEach((raw, i) => {
    const line = raw.trim();
    if (!line) return;
    const m = line.match(LINE);
    const seconds = m ? parseStamp(m[1]) : null;
    if (seconds == null) {
      issues.push({ line: i + 1, level: 'warn', message: `Line ${i + 1} has no timestamp at the start, so YouTube ignored it.` });
      return;
    }
    const title = (m[2] || '').trim();
    if (!title) issues.push({ line: i + 1, level: 'error', message: `Line ${i + 1} (${m[1]}) has no title.` });
    chapters.push({ line: i + 1, seconds, title });
  });

  if (!chapters.length) {
    issues.push({ level: 'error', message: 'No timestamps found. Each chapter goes on its own line, starting with its time, like 0:00 Intro.' });
    return { chapters, issues, valid: false, fixed: '' };
  }
  if (chapters[0].seconds !== 0) {
    issues.push({ line: chapters[0].line, level: 'error', message: `The first timestamp is ${clock(chapters[0].seconds)}, but chapters must start at 0:00.` });
  }
  const seen = new Map();
  chapters.forEach((c, i) => {
    if (seen.has(c.seconds)) {
      issues.push({ line: c.line, level: 'error', message: `Line ${c.line} has the same time as line ${seen.get(c.seconds)} (${clock(c.seconds)}).` });
    } else seen.set(c.seconds, c.line);
    const prev = chapters[i - 1];
    if (prev && c.seconds < prev.seconds) {
      issues.push({ line: c.line, level: 'error', message: `Line ${c.line} (${clock(c.seconds)}) is out of order: it comes before line ${prev.line} (${clock(prev.seconds)}).` });
    } else if (prev && c.seconds > prev.seconds && c.seconds - prev.seconds < 10) {
      issues.push({ line: prev.line, level: 'error', message: `The chapter at ${clock(prev.seconds)} lasts ${c.seconds - prev.seconds} seconds. Each chapter needs at least 10 seconds.` });
    }
  });
  if (chapters.length < 3) {
    issues.push({ level: 'error', message: `There are ${chapters.length} timestamps. YouTube needs at least 3.` });
  }
  if (duration > 0) {
    chapters.filter(c => c.seconds >= duration).forEach(c => issues.push({ line: c.line, level: 'error', message: `Line ${c.line} (${clock(c.seconds)}) is after the video ends (${clock(duration)}).` }));
    const last = [...chapters].filter(c => c.seconds < duration).sort((a, b) => a.seconds - b.seconds).pop();
    if (last && duration - last.seconds < 10) {
      issues.push({ line: last.line, level: 'error', message: `The last chapter starts ${duration - last.seconds} seconds before the end. It needs at least 10 seconds.` });
    }
  }

  // The fix: in time order, starting at 0:00 (a first chapter in the opening 30 s moves there, else an
  // Intro is added), one per time, inside the video, each at least 10 s long
  const sorted = [...chapters]
    .filter(c => c.title && (!duration || c.seconds < duration))
    .sort((a, b) => a.seconds - b.seconds || a.line - b.line);
  if (sorted.length && sorted[0].seconds > 0) {
    if (sorted[0].seconds <= 30) sorted[0] = { ...sorted[0], seconds: 0 };
    else sorted.unshift({ seconds: 0, title: 'Intro' });
  }
  const kept = [];
  sorted.forEach(c => {
    const prev = kept[kept.length - 1];
    if (prev && c.seconds - prev.seconds < 10) return;
    kept.push(c);
  });
  while (duration && kept.length > 1 && duration - kept[kept.length - 1].seconds < 10) kept.pop();
  const fixed = kept.map(c => `${clock(c.seconds)} ${c.title}`).join('\n');
  const valid = !issues.some(i => i.level === 'error');
  return { chapters, issues, valid, fixed };
}
